import { autoPlacement, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNls } from '../../nls/nlsProvider'
import type { TourStep } from './types'
import './tourguide.css'

const TARGET_PADDING = 8
const TARGET_TIMEOUT_MS = 3000

type SpotlightPortalProps = {
  step: TourStep
  stepIndex: number
  stepCount: number
  routeReady: boolean
  isNextEnabled: boolean
  onBack: () => void
  onNext: () => void
  onSkip: () => void
  onTargetTimeout: () => void
  onTargetVisibilityChange: (visible: boolean) => void
}

const paddedRect = (rect: DOMRect): DOMRect => {
  // Clamp to viewport AND guard against off-screen targets producing negative
  // dimensions (DOMRect happily accepts negative width; downstream styling
  // breaks).
  const left = Math.max(0, rect.x - TARGET_PADDING)
  const top = Math.max(0, rect.y - TARGET_PADDING)
  const right = Math.min(window.innerWidth, rect.right + TARGET_PADDING)
  const bottom = Math.min(window.innerHeight, rect.bottom + TARGET_PADDING)
  return new DOMRect(left, top, Math.max(0, right - left), Math.max(0, bottom - top))
}

export const SpotlightPortal = ({
  step,
  stepIndex,
  stepCount,
  routeReady,
  isNextEnabled,
  onBack,
  onNext,
  onSkip,
  onTargetTimeout,
  onTargetVisibilityChange,
}: SpotlightPortalProps) => {
  const [target, setTarget] = useState<HTMLElement | null>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [hasTimedOut, setHasTimedOut] = useState(false)
  const [hasFoundTarget, setHasFoundTarget] = useState(false)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const { t } = useNls()
  const timeoutMs = typeof step.targetTimeoutMs === 'number' ? step.targetTimeoutMs : TARGET_TIMEOUT_MS
  const titleId = useId()
  const middleware = useMemo(() => [
    offset(16),
    step.position === 'auto' ? autoPlacement({ padding: 12 }) : flip({ padding: 12 }),
    shift({ padding: 12 }),
  ], [step.position])
  const { refs, floatingStyles } = useFloating({
    strategy: 'fixed',
    placement: step.position && step.position !== 'auto' ? step.position : 'bottom',
    middleware,
    whileElementsMounted: autoUpdate,
  })

  useEffect(() => {
    let isDisposed = false
    if (!routeReady) return

    let currentTarget: HTMLElement | null = null
    let intersectionObserver: IntersectionObserver | null = null
    let resizeObserver: ResizeObserver | null = null
    let mutationObserver: MutationObserver | null = null
    let initialDiscoveryTimer: number | null = null
    let timedOut = false
    let everSeen = false

    const detachCurrentTarget = () => {
      intersectionObserver?.disconnect()
      resizeObserver?.disconnect()
      intersectionObserver = null
      resizeObserver = null
      currentTarget = null
    }
    const cancelInitialDiscoveryTimer = () => {
      if (initialDiscoveryTimer !== null) window.clearTimeout(initialDiscoveryTimer)
      initialDiscoveryTimer = null
    }
    const setVisible = (element: HTMLElement) => {
      cancelInitialDiscoveryTimer()
      everSeen = true
      currentTarget = element
      setHasFoundTarget(true)
      setTarget(element)
      refs.setReference(element)
      setRect(paddedRect(element.getBoundingClientRect()))
      onTargetVisibilityChange(true)
    }
    const observe = (element: HTMLElement) => {
      if (currentTarget === element) return
      detachCurrentTarget()
      currentTarget = element
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
      resizeObserver = new ResizeObserver(() => {
        if (currentTarget) setRect(paddedRect(currentTarget.getBoundingClientRect()))
      })
      resizeObserver.observe(element)
      intersectionObserver = new IntersectionObserver(([entry]) => {
        // If the element got detached from the DOM (e.g. React remount),
        // forget it so the MutationObserver can re-discover a fresh node.
        if (!element.isConnected) {
          detachCurrentTarget()
          setTarget(null)
          setRect(null)
          onTargetVisibilityChange(false)
          findTarget()
          return
        }
        const visible = entry.isIntersecting && entry.intersectionRatio > 0 && element.getClientRects().length > 0
        if (visible) setVisible(element)
        else {
          // Target merely scrolled out of view (still in DOM). Hide the
          // popover but DO NOT re-arm the discovery timeout — that's what
          // would otherwise abort critical steps when users scroll.
          setTarget(null)
          setRect(null)
          onTargetVisibilityChange(false)
        }
      }, { threshold: 0.01 })
      intersectionObserver.observe(element)
    }
    const findTarget = () => {
      if (isDisposed || timedOut) return
      if (currentTarget?.isConnected) return
      const element = document.querySelector<HTMLElement>(step.targetSelector)
      if (!element) return
      observe(element)
      // Once we've found a target, stop watching DOM mutations until/unless
      // it gets detached again. Reconnect inside the intersection callback if
      // that happens.
      mutationObserver?.disconnect()
      mutationObserver = null
    }

    // Single initial-discovery timer — only fires if the target never appears.
    // Once `everSeen` flips true (target was visible at least once), critical
    // steps can never time out from a scroll-out alone.
    initialDiscoveryTimer = window.setTimeout(() => {
      if (everSeen) return
      timedOut = true
      setHasTimedOut(true)
      onTargetTimeout()
    }, timeoutMs)

    findTarget()
    if (!currentTarget) {
      // Narrow scope: childList + subtree is enough to spot the target being
      // mounted; attribute changes (typing, class toggles) are irrelevant and
      // were causing per-keystroke MutationObserver thrash.
      mutationObserver = new MutationObserver(findTarget)
      mutationObserver.observe(document.body, { childList: true, subtree: true })
    }
    const handleViewportChange = () => {
      if (currentTarget) setRect(paddedRect(currentTarget.getBoundingClientRect()))
    }
    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)
    return () => {
      isDisposed = true
      cancelInitialDiscoveryTimer()
      mutationObserver?.disconnect()
      mutationObserver = null
      detachCurrentTarget()
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
      refs.setReference(null)
    }
  }, [onTargetTimeout, onTargetVisibilityChange, refs, routeReady, step.id, step.targetSelector, timeoutMs])

  useEffect(() => {
    if (!target || !popoverRef.current) return
    const popover = popoverRef.current
    const focusableSelector = 'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    // Don't autofocus for input-gated steps — stealing focus from the target
    // input is exactly the opposite of what the user is being asked to do.
    if (step.action !== 'input') popover.querySelector<HTMLElement>(focusableSelector)?.focus()
    const handleKeyDown = (event: KeyboardEvent) => {
      // Escape on the popover dismisses; Escape inside other inputs (closing
      // a select, dismissing autocomplete, etc.) must NOT kill the tour.
      if (event.key === 'Escape') {
        if (popover.contains(event.target as Node)) {
          event.preventDefault()
          onSkip()
        }
        return
      }
      if (event.key !== 'Tab') return
      // Focus trap only applies once focus is already inside the popover.
      if (!popover.contains(document.activeElement)) return
      const focusable = [...popover.querySelectorAll<HTMLElement>(focusableSelector)]
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onSkip, step.action, target])

  const aperture = rect ? {
    top: rect.top,
    left: rect.left,
    right: rect.right,
    bottom: rect.bottom,
    width: rect.width,
    height: rect.height,
  } : null

  const isWaiting = Boolean(typeof step.targetTimeoutMs === 'number' && !hasFoundTarget && !hasTimedOut)

  return createPortal(
    <>
      <AnimatePresence mode="wait">
        {target && aperture && (
          <motion.div key={step.id} className="tourGuidePortal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="tourGuideDimmer tourGuideDimmer--top" animate={{ height: aperture.top }} />
            <motion.div className="tourGuideDimmer tourGuideDimmer--left" animate={{ top: aperture.top, width: aperture.left, height: aperture.height }} />
            <motion.div className="tourGuideDimmer tourGuideDimmer--right" animate={{ top: aperture.top, left: aperture.right, height: aperture.height }} />
            <motion.div className="tourGuideDimmer tourGuideDimmer--bottom" animate={{ top: aperture.bottom }} />
            <motion.div
              className="tourGuideAperture"
              animate={{ top: aperture.top, left: aperture.left, width: aperture.width, height: aperture.height }}
              transition={{ type: 'spring', stiffness: 380, damping: 34 }}
              aria-hidden="true"
            />
            <motion.div
              ref={(node) => { popoverRef.current = node; refs.setFloating(node) }}
              className="tourGuidePopover"
              style={floatingStyles}
              // eslint-disable-next-line no-restricted-syntax -- tour spotlight popover anchored to the target via floating-ui
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              initial={{ opacity: 0, y: 8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <IconButton
                size="sm"
                variant="tertiary"
                className="tourGuideClose"
                onClick={onSkip}
                label="Close tour"
                icon={<CloseRoundedIcon fontSize="inherit" />}
              />
              <span className="tourGuideCounter">{stepIndex + 1} / {stepCount}</span>
              <h2 id={titleId}>{step.title}</h2>
              <p>{step.message}</p>
              {step.action && !isNextEnabled && step.action !== 'wait' && <span className="tourGuideGate">Complete the highlighted action to continue.</span>}
              {step.action === 'wait' && <span className="tourGuideGate">Preparing the next step…</span>}
              <div className="tourGuideActions">
                <Button type="button" variant="tertiary" size="sm" onClick={onBack} disabled={stepIndex === 0}>Back</Button>
                {stepCount > 4 && <Button type="button" variant="tertiary" size="sm" onClick={onSkip}>Skip</Button>}
                <Button type="button" variant="primary" size="sm" onClick={onNext} disabled={!isNextEnabled}>
                  {stepIndex === stepCount - 1 ? 'Finish' : 'Next'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {isWaiting && (
        <div
          key={`waiting-${step.id}`}
          className="tourGuideWaitingCard"
          role="status"
        >
          <h2>{step.title}</h2>
          <span className="tourGuideHint">{t('NLSTourGuideWaitingHint')}</span>
        </div>
      )}
    </>,
    document.body,
  )
}
